/**
 * JSON Schema definitions for the canonical poll upload shape.
 * Uses JSON Schema Draft 2020-12 compatible with Ajv.
 */

import {
  POLL_KINDS,
  SCHEMA_VERSION,
  VOTE_ANONYMIZATION_VALUES,
  VOTE_TYPES,
  VOTE_VISIBILITY_VALUES,
} from "./constants.js";

const isoDateTimePattern =
  "^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2})$";

const slotSchema = {
  type: "object",
  properties: {
    startUtcIso: { type: "string", pattern: isoDateTimePattern },
    endUtcIso: { type: "string", pattern: isoDateTimePattern },
  },
  required: ["startUtcIso", "endUtcIso"],
  additionalProperties: false,
};

const optionSchema = {
  type: "object",
  properties: {
    label: { type: "string", minLength: 1 },
    note: { type: "string" },
  },
  required: ["label"],
  additionalProperties: false,
};

const settingsSchema = {
  type: "object",
  properties: {
    voteType: { type: "string", enum: VOTE_TYPES },
    allowMultiple: { type: "boolean" },
    maxSelections: {
      oneOf: [{ type: "integer", minimum: 1 }, { type: "null" }],
    },
    allowWriteIn: { type: "boolean" },
    deadlineAt: {
      oneOf: [{ type: "string", pattern: isoDateTimePattern }, { type: "null" }],
    },
  },
  additionalProperties: false,
};

const questingGroupSchema = {
  type: "object",
  properties: {
    id: { type: "string", minLength: 1 },
    name: { type: "string", minLength: 1 },
  },
  additionalProperties: false,
};

const votePrivacyProperties = {
  voteVisibility: { type: "string", enum: VOTE_VISIBILITY_VALUES },
  voteAnonymization: { type: "string", enum: VOTE_ANONYMIZATION_VALUES },
  hideVoterIdentities: { type: "boolean" },
};

const basicPayloadProperties = {
  title: { type: "string", minLength: 1 },
  description: { type: "string" },
  questingGroup: questingGroupSchema,
  ...votePrivacyProperties,
  settings: settingsSchema,
  options: {
    type: "array",
    items: optionSchema,
    minItems: 2,
  },
};

const embeddedPollSchema = {
  type: "object",
  properties: {
    ...basicPayloadProperties,
    required: { type: "boolean" },
  },
  required: ["title", "options"],
  additionalProperties: false,
};

const sessionPayloadSchema = {
  type: "object",
  properties: {
    title: { type: "string" },
    description: { type: "string" },
    timezone: { type: "string", minLength: 1 },
    timezoneMode: { type: "string", enum: ["auto", "manual"] },
    questingGroup: questingGroupSchema,
    invitees: {
      type: "array",
      items: { type: "string", minLength: 1 },
    },
    allowLinkSharing: { type: "boolean" },
    ...votePrivacyProperties,
    slots: {
      type: "array",
      items: slotSchema,
      minItems: 1,
    },
    embeddedPolls: {
      type: "array",
      items: embeddedPollSchema,
    },
  },
  required: ["slots"],
  additionalProperties: false,
};

const basicPayloadSchema = {
  type: "object",
  properties: basicPayloadProperties,
  required: ["title", "options"],
  additionalProperties: false,
};

/**
 * Top-level envelope schema. Uses if/then/else for payload discrimination.
 */
export const pollUploadSchema = {
  type: "object",
  properties: {
    schemaVersion: { type: "string", const: SCHEMA_VERSION },
    pollKind: { type: "string", enum: POLL_KINDS },
    payload: { type: "object" },
  },
  required: ["schemaVersion", "pollKind", "payload"],
  additionalProperties: false,
  if: { properties: { pollKind: { const: "SESSION" } } },
  then: { properties: { payload: sessionPayloadSchema } },
  else: { properties: { payload: basicPayloadSchema } },
};

/**
 * Schema for add-on poll import: a single embedded poll object or an array.
 */
export const addonPollInputSchema = {
  oneOf: [
    embeddedPollSchema,
    { type: "array", items: embeddedPollSchema, minItems: 1 },
  ],
};

// Export sub-schemas for direct use in tests.
export { sessionPayloadSchema, basicPayloadSchema, embeddedPollSchema };
